import { useEffect, useState } from "react";

type S3Object = { key: string; size: number; lastModified?: string };

type ListResponse = {
  bucket: string;
  prefix: string;
  objects: S3Object[];
  nextContinuationToken?: string;
  isTruncated: boolean;
  error?: string;
};

type Props = { graphToken: string | null };

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function S3Browser({ graphToken }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<ListResponse | null>(null);
  const [tokens, setTokens] = useState<string[]>([]);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const load = async (continuationToken?: string) => {
    if (!graphToken) return;
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (continuationToken) params.set("continuationToken", continuationToken);
      const res = await fetch(`/api/s3/list?${params}`, {
        headers: { "x-graph-token": graphToken }
      });
      const body = (await res.json()) as ListResponse;
      if (!res.ok) {
        setErr(body.error ?? `HTTP ${res.status}`);
      } else {
        setData(body);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const download = async (key: string) => {
    if (!graphToken) return;
    setDownloadingKey(key);
    try {
      const res = await fetch(`/api/s3/download?key=${encodeURIComponent(key)}`, {
        headers: { "x-graph-token": graphToken }
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      window.open(body.url as string, "_blank", "noopener,noreferrer");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingKey(null);
    }
  };

  useEffect(() => {
    if (open && !data && graphToken) {
      load();
    }
  }, [open, graphToken]);

  const goNext = () => {
    if (!data?.nextContinuationToken) return;
    setTokens((prev) => [...prev, data.nextContinuationToken!]);
    load(data.nextContinuationToken);
  };

  const goPrev = () => {
    if (tokens.length === 0) {
      load();
      return;
    }
    const next = tokens.slice(0, -1);
    setTokens(next);
    load(next[next.length - 1]);
  };

  const filtered = data?.objects.filter((o) =>
    o.key.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer"
        }}
        onClick={() => setOpen(!open)}
      >
        <h2 style={{ margin: 0 }}>3. Previous exports in S3</h2>
        <button className="secondary" type="button">
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 16 }}>
          <div className="row" style={{ marginBottom: 12 }}>
            <input
              type="text"
              placeholder="Filter by key…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            <button
              className="secondary"
              type="button"
              onClick={() => {
                setTokens([]);
                load();
              }}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {err && (
            <div className="error" style={{ marginBottom: 12 }}>
              {err}{" "}
              <button
                type="button"
                className="secondary"
                onClick={() => load(tokens[tokens.length - 1])}
                style={{ marginLeft: 8 }}
              >
                Retry
              </button>
            </div>
          )}

          {loading && !data && <div className="loading-bar" />}

          {data && filtered && (
            <>
              <p className="muted small">
                {data.bucket}/{data.prefix} — {filtered.length} of{" "}
                {data.objects.length} shown{data.isTruncated ? ", more available" : ""}
              </p>
              <div className="team-table-wrap">
                <table className="team-table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th style={{ width: 110 }}>Size</th>
                      <th style={{ width: 180 }}>Last modified</th>
                      <th style={{ width: 110 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((o) => (
                      <tr key={o.key}>
                        <td><code className="small">{o.key}</code></td>
                        <td>{fmtBytes(o.size)}</td>
                        <td>{fmtDate(o.lastModified)}</td>
                        <td>
                          <button
                            type="button"
                            className="secondary"
                            disabled={downloadingKey === o.key}
                            onClick={() => download(o.key)}
                          >
                            {downloadingKey === o.key ? "…" : "Download"}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={4} className="muted" style={{ textAlign: "center" }}>
                          No objects.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="row" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="secondary"
                  onClick={goPrev}
                  disabled={loading || tokens.length === 0}
                >
                  ← Prev
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={goNext}
                  disabled={loading || !data.isTruncated}
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
